'use client';
import { useMediaQuery } from '@react-hookz/web';

import { routes } from '~constants/routes';
import { usePaywallContext } from '~context/paywall';
import { PaymentReason } from '~features/annals/enums/payment';
import annalsCheckout from '~features/annals/hooks/checkout';
import { useSession } from '~hooks/useSession';
import { useSluggedPathname } from '~hooks/useSluggedPathname';
import { buildFullUrl } from '~shared/utils/images';

import { usePromoEventNeurons } from '../../hooks/usePromoEventNeurons';
import { usePromoEventSubscription } from '../../hooks/usePromoEventSubscription';
import { ClickableArea, Container, PromoEventImage } from './styles';

export const PromoEventButton = () => {
	const reasonType = annalsCheckout.usePaymentReasonType();
	const isMobile = useMediaQuery('screen and (max-width: 767px)');

	const { isDemoUser } = useSession();
	const { openPaywall } = usePaywallContext();
	const [pathname, sluggedPathname] = useSluggedPathname();
	const hideOnMobileSingleChat =
		isMobile &&
		sluggedPathname.includes('botId') &&
		sluggedPathname.includes('chats');
	const hideOnMobileBotWizard =
		isDemoUser && isMobile && pathname === routes.generate.character;

	const {
		promoMembershipActive,
		isLoading: isMembershipDataLoading,
		data: membershipData,
		activeOnPages: membershipActiveOnPages,
	} = usePromoEventSubscription();

	const {
		promoNeuronsActive,
		isLoading: isNeuronsDataLoading,
		data: neuronsData,
		activeOnPages: neuronsActiveOnPages,
	} = usePromoEventNeurons();

	const offerType = promoMembershipActive
		? 'membership'
		: promoNeuronsActive
		? 'neurons'
		: undefined;

	let image;
	let imageAlt;
	let defaultPromocode;
	switch (offerType) {
		case 'membership':
			image = membershipData.buttonImageUrl;
			imageAlt = 'Promo event membership button';
			defaultPromocode = membershipData.promocode;
			break;
		case 'neurons':
			image = neuronsData.buttonImageUrl;
			imageAlt = 'Promo event neurons button';
			defaultPromocode = neuronsData.promocode;
			break;
		default:
			break;
	}
	const handleClick = openPaywall({
		reason: PaymentReason.PromoEvent,
		reasonType,
		prefer: promoMembershipActive ? 'subscription' : 'neurons',
		defaultPromocode,
	});

	if (
		process.env.NEXT_PUBLIC_HIDE_PROMO_EVENT_BUTTON ||
		isMembershipDataLoading ||
		isNeuronsDataLoading ||
		!image ||
		!imageAlt ||
		hideOnMobileSingleChat ||
		hideOnMobileBotWizard
	) {
		return null;
	}

	if (
		!!membershipActiveOnPages &&
		offerType === 'membership' &&
		!membershipActiveOnPages.includes(sluggedPathname)
	) {
		return null;
	}

	if (
		!!neuronsActiveOnPages &&
		offerType === 'neurons' &&
		!neuronsActiveOnPages.includes(sluggedPathname)
	) {
		return null;
	}

	return (
		<Container>
			<PromoEventImage
				src={buildFullUrl(image)}
				width={200}
				height={200}
				alt={imageAlt}
				quality={75}
			/>
			<ClickableArea onClick={handleClick} />
		</Container>
	);
};
